import { useEffect, useState, type ReactNode } from 'react';
import { Mark, SignIn, ThemeToggle } from '@drbell/ui';
import { api, ApiError, type Channel, type InviteAccepted, type InvitePreview, type Me } from '../api.ts';
import {
  approveInvite, failureOf, InviteCard, inviterOf, patientOf, rejectInvite, type AnswerFailure,
} from './InviteCard.tsx';

/**
 * /i/:token — the page an invitation link opens.
 *
 * DrBell cannot message somebody who has not agreed to hear from it, so the
 * inviter sends this link from their own WhatsApp or SMS. Whoever opens it
 * may have no account, no app, and no idea what DrBell is: the details are
 * shown before sign-in, and signing in is asked for only to answer.
 */

/** `/i/<token>`, or null. A trailing slash is what a chat app sometimes adds. */
export function inviteTokenFromPath(pathname: string): string | null {
  const m = /^\/i\/([^/]+)\/?$/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}

/*
 * Coming back after signing in.
 *
 * A code sign-in happens on this page, so the address never changes. Google
 * and Apple do not: the shared sign-in sends the browser away and back to
 * `/`, and the invitation would be lost. So the token is held for the tab
 * while that happens and picked up on the way back — briefly, so an
 * abandoned sign-in does not bounce somebody to an old invitation a day
 * later.
 */
const RETURN_KEY = 'drbell.invite-return';
const RETURN_FOR_MS = 30 * 60 * 1000;

function rememberForSignIn(token: string) {
  try {
    sessionStorage.setItem(RETURN_KEY, JSON.stringify({ token, at: Date.now() }));
  } catch {
    /* storage refused: a code sign-in still works, it never leaves the page */
  }
}

/** The invitation a sign-in was started for, once. */
export function takeInviteReturn(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const { token, at } = JSON.parse(raw) as { token?: string; at?: number };
    return token && at && Date.now() - at < RETURN_FOR_MS ? token : null;
  } catch {
    return null;
  }
}

type Answered =
  | { kind: 'approved'; patientId: string; self: boolean }
  | { kind: 'rejected'; self: boolean };

export function InvitePage({
  token, me, onSignedIn, onDone,
}: {
  token: string;
  /** null: signed out. */
  me: Me | null;
  onSignedIn: () => void;
  /** Into the app's main screen, on the record just joined if there is one. */
  onDone: (patientId?: string) => void;
}) {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [ended, setEnded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<AnswerFailure | null>(null);
  const [answered, setAnswered] = useState<Answered | null>(null);

  useEffect(() => {
    setPreview(null);
    setEnded(false);
    setLoadError(null);
    api
      .get<InvitePreview>(`/api/invites/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 404 || e.status === 410)) setEnded(true);
        else setLoadError(e instanceof Error ? e.message : String(e));
      });
  }, [token]);

  /*
   * Signed in now: the sign-in the token was held for is over. Dropped here
   * as well as on the way back from Google, or a code sign-in would leave it
   * behind and "Go to DrBell" would bounce straight back to this page.
   */
  useEffect(() => {
    if (!me) return;
    setSigningIn(false);
    takeInviteReturn();
  }, [me]);

  const approve = async (channels: Channel[] | undefined) => {
    setBusy(true);
    setFailure(null);
    try {
      const r: InviteAccepted = await approveInvite(token, channels, preview?.ringsHere);
      setAnswered({ kind: 'approved', patientId: r.patientId, self: 'linkedAsSelf' in r });
    } catch (e) {
      setFailure(failureOf(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await rejectInvite(token);
      setAnswered({ kind: 'rejected', self: preview?.role === 'self' });
    } catch (e) {
      setFailure(failureOf(e));
    } finally {
      setBusy(false);
    }
  };

  const hint = preview?.invitePhoneHint ?? null;

  if (signingIn && !me) {
    return (
      <>
        <div className="mx-auto max-w-md px-4 pt-4">
          <button
            onClick={() => setSigningIn(false)}
            className="min-h-11 rounded-lg px-2 text-sm text-(--muted) hover:text-(--fg)"
          >
            ← Back to the invitation
          </button>
        </div>
        <SignIn
          title={hint ? `Sign in with ${hint} to answer` : 'Sign in to answer'}
          subtitle={
            preview
              ? `${inviterOf(preview)} sent this invitation to that number. Sign in with it to approve or reject.`
              : 'Use the number the invitation was sent to.'
          }
          onSignedIn={onSignedIn}
        />
      </>
    );
  }

  const goHome = (
    <button
      onClick={() => onDone(answered?.kind === 'approved' ? answered.patientId : undefined)}
      className="min-h-11 w-full rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
    >
      Go to DrBell
    </button>
  );

  let body;
  if (ended || failure?.kind === 'ended') {
    body = (
      <Panel title="This invitation has ended or was withdrawn.">
        <p className="text-sm text-(--muted)">
          If you still need it, ask whoever sent it for a new one.
        </p>
        <div className="mt-5">{goHome}</div>
      </Panel>
    );
  } else if (loadError) {
    body = (
      <Panel title="Could not open this invitation">
        <p role="alert" className="text-sm text-red-500">{loadError}</p>
        <button
          onClick={() => location.reload()}
          className="mt-5 min-h-11 w-full rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
        >
          Try again
        </button>
      </Panel>
    );
  } else if (!preview) {
    body = <p className="text-(--muted)">Opening the invitation…</p>;
  } else if (answered) {
    const inviter = inviterOf(preview);
    const patient = patientOf(preview);
    body = (
      <Panel
        title={
          answered.kind === 'approved'
            ? answered.self
              ? 'Done. Your reminders will come to you.'
              : `Done. You now help look after ${patient}.`
            : 'You rejected the invitation.'
        }
      >
        <p className="text-sm text-(--muted)">
          {answered.kind === 'approved'
            ? answered.self
              ? `${inviter} can still see your medicines and is told if you miss a dose.`
              : `You can see ${patient}’s medicines and doses now. ${inviter} will be told you approved.`
            : answered.self
              ? `${inviter} keeps the record and this phone is not linked. ${inviter} will be told.`
              : `Nothing about ${patient} is shared with you. ${inviter} will be told.`}
        </p>
        <div className="mt-5">{goHome}</div>
      </Panel>
    );
  } else {
    body = (
      <InviteCard
        token={token}
        preview={preview}
        showAppLink
        busy={busy}
        onApprove={me ? (c) => void approve(c) : undefined}
        onReject={me ? () => void reject() : undefined}
        problem={failure && <Problem failure={failure} hint={hint} me={me} />}
        signIn={
          <button
            onClick={() => {
              rememberForSignIn(token);
              setSigningIn(true);
              window.scrollTo(0, 0);
            }}
            className="min-h-11 w-full rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
          >
            {hint ? `Sign in with ${hint} to answer` : 'Sign in to answer'}
          </button>
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-(--bg)">
      <header className="mx-auto flex max-w-md items-center gap-3 px-4 pt-6">
        <Mark className="h-9 w-9 shrink-0" />
        <span className="font-semibold">DrBell</span>
        <ThemeToggle className="ml-auto" />
      </header>
      <main className="mx-auto w-full max-w-md px-4 pb-16 pt-8">{body}</main>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-(--line) bg-(--bg) p-5 sm:p-6">
      <h1 className="mb-2 text-xl font-semibold tracking-tight">{title}</h1>
      {children}
    </section>
  );
}

/** Why the answer did not go through, and the way out of it. */
function Problem({ failure, hint, me }: { failure: AnswerFailure; hint: string | null; me: Me | null }) {
  const box = 'rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500';
  if (failure.kind === 'no_channel') return <p role="alert" className={box}>Keep at least one way to be told.</p>;
  if (failure.kind === 'phone_mismatch') {
    return (
      <div role="alert" className={box}>
        <p>
          This invitation was sent to {hint ?? 'a different number'}
          {me?.user?.phoneNumber ? `, and you are signed in as ${me.user.phoneNumber}` : ''}. Only that
          number can answer it.
        </p>
        <button
          onClick={() => { void api.post('/api/auth/sign-out').then(() => location.reload()); }}
          className="mt-2 min-h-11 rounded-lg border border-red-500/40 px-3 text-sm hover:bg-red-500/10"
        >
          Sign out and use {hint ?? 'that number'}
        </button>
      </div>
    );
  }
  if (failure.kind === 'other') return <p role="alert" className={box}>{failure.message}</p>;
  return null;
}
